import { Person } from '@/graphql/generated/types';
import { Box } from '@/ui/box/box.component';
import { Button } from '@/ui/button/button.component';
import { useConfirm, useModal } from '@/ui/modal/modal.hooks';
import { error, success } from '@/ui/toaster/toast';
import React, { useCallback, useState } from 'react';
import {
  CustomerFinder,
  CustomerItemType,
} from '../customer-finder/customer-finder.component';
import { getPersonDescription } from '../utils';
import {
  useAnonymizePersonMutation,
  useDeletePersonMutation,
  useLinkDuplicatesMutation,
} from './actions.generated';

export function usePersonActions(
  callback: (action: 'anonymize' | 'delete') => void,
) {
  const [deletePerson] = useDeletePersonMutation();
  const [anonymizePerson] = useAnonymizePersonMutation();
  const [modalDelete, askDelete] = useConfirm(
    async (id: string) => {
      try {
        await deletePerson({
          variables: {
            id,
          },
        });
        success('Die Person und alle verknüpften Elemente wurden gelöscht.');
        callback('delete');
      } catch (e: any) {
        console.error(e);
        error('Beim Löschen ist ein Fehler aufgetreten.', e.message);
      }
    },
    { meaning: 'error', title: 'Person löschen' },
  );
  const [modalAnonymize, askAnonymize] = useConfirm(
    async (id: string) => {
      try {
        await anonymizePerson({
          variables: {
            id,
          },
        });
        success(
          'Die Person wurde anonymisiert',
          'Die Person und verknüpfte Objekte wurden vollständig anonymisiert. Diese Person wird in der Übersicht nicht mehr gelistet.',
        );
        callback('anonymize');
      } catch (e: any) {
        console.error(e);
        error('Beim Anonymisieren ist ein Fehler aufgetreten.', e.message);
      }
    },
    {
      meaning: 'error',
      title: 'Person löschen (anonymisieren)',
    },
  );

  const askForAnonymize = useCallback(
    (
      person: Pick<
        Person,
        'id' | 'firstName' | 'lastName' | 'gender' | 'title'
      >,
    ) => {
      askAnonymize(
        <>
          <p>
            Mit dieser Aktion wird die Person datenschutzkonform aus der
            Datenbank entfernt bzw. alle personenbezogenen Daten gelöscht.
            Buchungen, Bestellungen u.a. bleiben erhalten. Statistiken werden
            nicht verändert.
          </p>
          <p>
            Möchtest du die Person <em>{getPersonDescription(person)}</em>{' '}
            löschen (anonymisieren)?
          </p>
        </>,
        person.id,
      );
    },
    [],
  );

  const askForDelete = useCallback(
    (
      person: Pick<
        Person,
        'id' | 'firstName' | 'lastName' | 'gender' | 'title'
      >,
    ) => {
      askDelete(
        <>
          <p>
            Alle verknüpften Bestellungen, Buchungen und Anfragen werden
            ebenfalls gelöscht.
          </p>
          <p>Diese Aktion lässt sich nicht rückgängig machen!</p>
          <p>
            Statistiken werden durch diese Aktion beeinflusst. Wenn du lediglich
            die Person / Kund:in löschen, ausblenden und datenschutzkonform
            anonymisieren möchtest, verwende die andere Methode.
          </p>
          <p>
            Bist du sicher, dass du die Person{' '}
            <em>{getPersonDescription(person)}</em> löschen möchtest?
          </p>
        </>,

        person.id,
      );
    },
    [],
  );
  return { modalAnonymize, modalDelete, askForAnonymize, askForDelete };
}

export function useLinkAsDuplicate(callback: () => void) {
  const [selectedPerson, setSelectedPerson] = useState<CustomerItemType | null>(
    null,
  );
  const [sourcePersonId, setSourcePersonId] = useState<string | null>(null);
  const [linkDuplicates] = useLinkDuplicatesMutation();

  const handleCancel = useCallback(() => {
    close();
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!sourcePersonId || !selectedPerson) return;
    try {
      await linkDuplicates({
        variables: { person1Id: sourcePersonId, person2Id: selectedPerson.value },
      });
      success('Duplikat verknüpft', 'Die Personen wurden als Duplikat markiert.');
      close();
      setSelectedPerson(null);
      callback();
    } catch (e: any) {
      error('Fehler beim Verknüpfen', e.message);
    }
  }, [sourcePersonId, selectedPerson, linkDuplicates]);

  const [modalLinkDuplicate, { open, close }] = useModal(
    <Box title="Als Duplikat markieren">
      <p>
        Wähle den Kunden, mit dem dieser Eintrag zusammengeführt werden soll:
      </p>
      <CustomerFinder
        value={selectedPerson}
        onChange={(item) => setSelectedPerson(item ?? null)}
      />
      <div
        style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}
      >
        <Button label="Abbrechen" onClick={handleCancel} />
        <Button label="Verknüpfen" primary onClick={handleConfirm} />
      </div>
    </Box>,
    { closeOnDimmerClick: false, width: '40vw' },
  );

  const askForLink = useCallback((personId: string) => {
    setSourcePersonId(personId);
    setSelectedPerson(null);
    open();
  }, []);

  return { modalLinkDuplicate, askForLink };
}
